import React from 'react'
import { useIsEditing } from './blocks'
import { usePhotoMutation } from './QueryClient'
import { useDropzone } from 'react-dropzone'
import { cn } from '~/lib/utils'
import { useToast } from '~/lib/ui/toast'
import { Button } from '~/lib/ui/button'
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline'

export const ProfilePhotoDisplay = React.forwardRef(
  ({ photoUrl, placeholder, className, children, ...props }, ref) => {
    const baseClassName =
      'group relative flex size-24 flex-row items-center justify-center rounded-full border-4 border-[#ececf0]'
    const containerClassName = cn(baseClassName, className)
    return (
      <div className={containerClassName} ref={ref} {...props}>
        <div className="border-1 absolute flex h-full w-full flex-row items-center justify-center rounded-full border-[#000000] bg-intoo-blue-medium text-white">
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Profile Photo"
              className="border-3 h-full w-full rounded-full border-white object-cover"
            />
          ) : (
            placeholder
          )}
        </div>
        {children}
      </div>
    )
  },
)
ProfilePhotoDisplay.displayName = 'ProfilePhotoDisplay'

export const ProfilePhoto = ({ photoUrl, placeholder }) => {
  const { isEditing } = useIsEditing()

  if (isEditing) {
    return <ProfilePhotoEdit photoUrl={photoUrl} placeholder={placeholder} />
  }
  return <ProfilePhotoDisplay photoUrl={photoUrl} placeholder={placeholder} />
}

const errorMessages = {
  file_invalid_type: '(Files must be in .jpg, .jpeg, .gif, or .png format)',
  file_too_large: '(File is too large. Maximum size is 5 MB)',
}

export const ProfilePhotoEdit = ({ photoUrl, placeholder }) => {
  const photoMutation = usePhotoMutation()
  const onDropAccepted = (acceptedFiles) => {
    photoMutation.mutate(acceptedFiles[0])
  }
  const { toast } = useToast()
  const onDropRejected = (fileRejections) => {
    const {
      file: { type },
      errors,
    } = fileRejections[0]
    const description = errors
      .map((e) => ` • ${errorMessages[e.code] || e.message}`)
      .join('\n')
    if (type) {
      toast({
        description,
        title: 'Could not upload photo',
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDropAccepted,
    onDropRejected,
    maxFiles: 1,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
    maxSize: 5_000_000,
    disabled: photoMutation.isPending,
  })
  const removePhoto = (e) => {
    e.stopPropagation()
    photoMutation.mutate(null)
  }
  return (
    <div className="flex flex-row items-center gap-x-8">
      <ProfilePhotoDisplay
        photoUrl={photoUrl}
        placeholder={placeholder}
        className={
          isDragActive
            ? 'border-dashed border-intoo-blue-medium'
            : 'cursor-pointer'
        }
        {...getRootProps()}
      >
        <input {...getInputProps()} />
      </ProfilePhotoDisplay>
      <div className="flex flex-col gap-y-2">
        <div className="flex flex-row items-center gap-x-4">
          <Button
            size="sm"
            variant="outline"
            className="border-black/10 px-2"
            onClick={open}
            disabled={photoMutation.isPending}
          >
            <ArrowUpTrayIcon className="mr-2 size-5" />
            Upload Photo
          </Button>
          {photoUrl && (
            <Button
              onClick={removePhoto}
              disabled={photoMutation.isPending}
              variant="ghost"
              size="sm"
            >
              Remove Photo
            </Button>
          )}
        </div>
        <div className="text-[#717182]">JPG, PNG, or GIF. Max size 5 MB</div>
      </div>
    </div>
  )
}
